import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useAuthActions } from "@convex-dev/auth/react";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, ArrowLeft, Mail } from "lucide-react";
import {
  AuthLeftPanel,
  AuthRightPanel,
  AuthMobileLogo,
} from "@/components/auth/auth-layout";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

export const Route = createFileRoute("/_public/auth/sign-up")({
  component: SignUpPage,
});

type SignUpStep = "credentials" | "verify";

function SignUpPage() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();

  const [step, setStep] = useState<SignUpStep>("credentials");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    code: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    code: "",
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return "El correo electrónico es requerido";
    }
    if (!emailRegex.test(email)) {
      return "Ingresa un correo electrónico válido";
    }
    return "";
  };

  const validatePassword = (password: string) => {
    if (!password) {
      return "La contraseña es requerida";
    }
    if (password.length < 8) {
      return "La contraseña debe tener al menos 8 caracteres";
    }
    return "";
  };

  const validateConfirmPassword = (
    password: string,
    confirmPassword: string,
  ) => {
    if (!confirmPassword) {
      return "Debes confirmar tu contraseña";
    }
    if (password !== confirmPassword) {
      return "Las contraseñas no coinciden";
    }
    return "";
  };

  const handleInputChange = (field: string, value: string) => {
    // Normalize email to lowercase to prevent login issues
    const normalizedValue =
      field === "email" ? value.toLowerCase().trim() : value;
    setFormData((prev) => ({ ...prev, [field]: normalizedValue }));

    let error = "";
    if (field === "email") {
      error = validateEmail(value);
    } else if (field === "password") {
      error = validatePassword(value);
      if (formData.confirmPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: validateConfirmPassword(
            value,
            formData.confirmPassword,
          ),
        }));
      }
    } else if (field === "confirmPassword") {
      error = validateConfirmPassword(formData.password, value);
    } else if (field === "code") {
      error = "";
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSendCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const confirmPasswordError = validateConfirmPassword(
      formData.password,
      formData.confirmPassword,
    );

    if (emailError || passwordError || confirmPasswordError) {
      setErrors({
        ...errors,
        email: emailError,
        password: passwordError,
        confirmPassword: confirmPasswordError,
      });
      return;
    }

    setIsLoading(true);

    try {
      // First, send OTP code to verify email
      const otpFormData = new FormData();
      otpFormData.append("email", formData.email);

      await signIn("resend-otp", otpFormData);
      toast.success("Código de verificación enviado a tu correo");
      setStep("verify");
    } catch (error: any) {
      console.error("Error enviando codigo:", error);

      if (
        error?.message?.includes("already exists") ||
        error?.message?.includes("duplicate")
      ) {
        toast.error(
          "Este correo ya está registrado. Usa 'Iniciar Sesión' u 'Olvidé mi contraseña'.",
        );
        setErrors((prev) => ({
          ...prev,
          email: "Este correo ya está en uso",
        }));
      } else {
        toast.error("Error al crear la cuenta. Intenta nuevamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);

    try {
      const otpFormData = new FormData();
      otpFormData.append("email", formData.email);

      await signIn("resend-otp", otpFormData);
      toast.success("Código reenviado a tu correo");
    } catch (error: any) {
      console.error("Error reenviando codigo:", error);
      toast.error("Error al reenviar el código. Intenta nuevamente.");
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formData.code.length !== 8) {
      setErrors((prev) => ({ ...prev, code: "Ingresa el código completo" }));
      return;
    }

    setIsLoading(true);

    try {
      // Verify OTP code and create account with password
      const verifyFormData = new FormData();
      verifyFormData.append("email", formData.email);
      verifyFormData.append("code", formData.code);

      // First verify the OTP
      await signIn("resend-otp", verifyFormData);

      // Then set the password for the account
      const passwordFormData = new FormData();
      passwordFormData.append("email", formData.email);
      passwordFormData.append("password", formData.password);
      passwordFormData.append("flow", "signUp");

      await signIn("password", passwordFormData);
      toast.success("Cuenta creada exitosamente");
      navigate({ to: "/dashboard" });
    } catch (error: any) {
      console.error("Error verificando codigo:", error);

      if (
        error?.message?.includes("Invalid") ||
        error?.message?.includes("invalid") ||
        error?.message?.includes("expired")
      ) {
        toast.error("Código inválido o expirado. Solicita uno nuevo.");
        setErrors((prev) => ({
          ...prev,
          code: "Código inválido o expirado",
        }));
      } else {
        toast.error("Error al verificar el código. Intenta nuevamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep("credentials");
    setFormData((prev) => ({ ...prev, code: "" }));
    setErrors((prev) => ({ ...prev, code: "" }));
  };

  const renderCredentialsStep = () => (
    <form onSubmit={handleSendCode} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Correo electrónico</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="tu@email.com"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          disabled={isLoading}
          className={errors.email ? "border-destructive" : ""}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Contraseña</Label>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Mínimo 8 caracteres"
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            disabled={isLoading}
            className={errors.password ? "border-destructive" : ""}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 z-10 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Repite tu contraseña"
            value={formData.confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            disabled={isLoading}
            className={errors.confirmPassword ? "border-destructive" : ""}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 z-10 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            disabled={isLoading}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        {errors.confirmPassword && (
          <p className="text-sm text-destructive">{errors.confirmPassword}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Spinner className="mr-2 h-4 w-4" />
            Enviando código...
          </>
        ) : (
          <>
            Continuar
            <ArrowRight className="ml-2 size-4" />
          </>
        )}
      </Button>
    </form>
  );

  const renderVerifyStep = () => (
    <form onSubmit={handleVerifyCode} className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary" />
          </div>
        </div>

        <div className="text-center space-y-1">
          <p className="text-sm text-muted-foreground">
            Enviamos un código de 8 dígitos a
          </p>
          <p className="font-medium">{formData.email}</p>
        </div>

        <div className="flex flex-col items-center space-y-2">
          <Label htmlFor="code">Código de verificación</Label>
          <InputOTP
            maxLength={8}
            value={formData.code}
            onChange={(value) => handleInputChange("code", value)}
            disabled={isLoading}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
              <InputOTPSlot index={6} />
              <InputOTPSlot index={7} />
            </InputOTPGroup>
          </InputOTP>
          {errors.code && (
            <p className="text-sm text-destructive">{errors.code}</p>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <Button
          type="submit"
          className="w-full"
          disabled={isLoading || formData.code.length !== 8}
        >
          {isLoading ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Verificando...
            </>
          ) : (
            <>
              Verificar y crear cuenta
              <ArrowRight className="ml-2 size-4" />
            </>
          )}
        </Button>

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleBack}
            disabled={isLoading}
          >
            <ArrowLeft className="mr-2 size-4" />
            Volver
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleResendCode}
            disabled={isLoading || isResending}
          >
            {isResending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Reenviando...
              </>
            ) : (
              "Reenviar código"
            )}
          </Button>
        </div>
      </div>
    </form>
  );

  const getStepTitle = () => {
    switch (step) {
      case "credentials":
        return "Crea tu cuenta";
      case "verify":
        return "Verifica tu correo";
      default:
        return "Crea tu cuenta";
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case "credentials":
        return "Completa el formulario para comenzar";
      case "verify":
        return "Ingresa el código que enviamos a tu correo";
      default:
        return "";
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <AuthLeftPanel
        headline="Empieza a gestionar tus envíos hoy"
        subtitle="Crea tu cuenta y accede a todas las herramientas que necesitas para gestionar paquetes, consolidados y entregas."
        badge="Comienza en minutos"
      />

      <AuthRightPanel>
        <div className="mb-8 text-center lg:text-left">
          <AuthMobileLogo />
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            {getStepTitle()}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {getStepSubtitle()}
          </p>
        </div>

        {step === "credentials" && renderCredentialsStep()}
        {step === "verify" && renderVerifyStep()}

        <div className="mt-6 text-center text-sm text-muted-foreground">
          ¿Ya tienes cuenta?{" "}
          <Link
            to="/auth/sign-in"
            className="text-primary hover:underline font-medium"
          >
            Inicia sesión
          </Link>
        </div>
      </AuthRightPanel>
    </div>
  );
}
